import { useEffect, type RefObject } from 'react';

const ROTATION_FORWARD_KEYS = new Set(['ArrowLeft', 'ArrowRight', ' ', 'Spacebar']);

function focusMonitoringIframe(iframe: HTMLIFrameElement): void {
  try {
    iframe.focus({ preventScroll: true });
  } catch {
    try {
      iframe.focus();
    } catch {
      /* ignore */
    }
  }
  try {
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (doc?.body) {
      if (!doc.body.hasAttribute('tabindex')) {
        doc.body.setAttribute('tabindex', '-1');
      }
      doc.body.focus({ preventScroll: true });
    }
    win?.focus();
  } catch {
    /* cross-origin or not ready */
  }
}

function dispatchKeyToIframe(iframe: HTMLIFrameElement, ev: KeyboardEvent): void {
  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) return;
  const init: KeyboardEventInit = {
    key: ev.key,
    code: ev.code,
    location: ev.location,
    ctrlKey: ev.ctrlKey,
    shiftKey: ev.shiftKey,
    altKey: ev.altKey,
    metaKey: ev.metaKey,
    repeat: ev.repeat,
    bubbles: true,
    cancelable: true,
  };
  const forwarded = new KeyboardEvent('keydown', init);
  doc.dispatchEvent(forwarded);
  if (forwarded.defaultPrevented) {
    ev.preventDefault();
    ev.stopPropagation();
  }
}

type Options = {
  /** 순환 화면용 ←/→/Space를 부모 창에서 iframe으로 전달 */
  forwardRotationKeys?: boolean;
};

/**
 * srcDoc iframe 모니터링 팝업 — 로드 직후 키보드가 동작하도록 포커스·키 전달
 */
export function useMonitoringPopupIframeKeyboard(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  enabled: boolean,
  options?: Options
): void {
  const forwardRotationKeys = options?.forwardRotationKeys ?? false;

  useEffect(() => {
    if (!enabled) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    const runFocus = () => focusMonitoringIframe(iframe);
    runFocus();
    const t1 = window.setTimeout(runFocus, 80);
    const t2 = window.setTimeout(runFocus, 350);

    const onParentFocus = () => runFocus();
    window.addEventListener('focus', onParentFocus);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('focus', onParentFocus);
    };
  }, [enabled, iframeRef]);

  useEffect(() => {
    if (!enabled || !forwardRotationKeys) return;

    const onKeyDown = (ev: KeyboardEvent) => {
      if (!ROTATION_FORWARD_KEYS.has(ev.key)) return;
      const tag = (ev.target as HTMLElement | null)?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const iframe = iframeRef.current;
      if (!iframe) return;
      const doc = iframe.contentDocument;
      if (!doc) return;

      if (doc.hasFocus()) return;

      ev.preventDefault();
      ev.stopPropagation();
      dispatchKeyToIframe(iframe, ev);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, forwardRotationKeys, iframeRef]);
}
